import { useMemo, useState } from 'react'

import { Booking } from '../../models/Booking'
import { Option } from '../../models/Option'
import { Schedule } from '../../models/Schedule'
import { isDayChecked } from '../../utils/dates'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import { Chip } from '../common/fields/Chip'
import { Select } from '../common/fields/Select'
import { WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    isOpen: boolean
    booking: Booking
    onClose: () => void
    onDone: (schedule?: Schedule) => void
}

export function ScheduleModal({ isOpen, booking, onClose, onDone }: Props) {
    const [schedule, setSchedule] = useState(
        (booking.schedule ?? new Schedule()).clone({ date: booking.date })
    )

    const intervalOptions = useMemo(() => {
        return [
            new Option(undefined, { id: '', label: "Don't repeat" }),
            ...Schedule.intervalStrings(),
        ].map((option) => ({
            id: option.id ?? '',
            title: option.label ?? '',
        }))
    }, [])

    const instancesOptions = useMemo(() => {
        return Schedule.instanceStrings(Schedule.SCHEDULE_CAPACITY).map((option) => ({
            id: option.id ?? '',
            title: option.label ?? '',
        }))
    }, [])

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="large"
            title="Schedule"
            footer={
                <Button
                    intent="primary"
                    disabled={!!schedule.interval && !schedule.dow && !schedule.wom}
                    onClick={() => {
                        onDone(schedule.interval ? schedule : undefined)
                        onClose()
                    }}
                >
                    Done
                </Button>
            }
        >
            <div className="my-8 flex flex-col gap-8">
                <WorkflowPage.IconSection title="Repeat every" icon="repeat.large">
                    <Select
                        size="medium"
                        selectedOptionID={
                            schedule.interval
                                ? `${schedule.interval}${schedule.frequency === 'monthly' ? 'm' : ''}`
                                : ''
                        }
                        options={intervalOptions}
                        onSelectOption={(option) => {
                            const updatedSchedule = schedule.clone({
                                interval: option.id ? parseInt(option.id) : undefined,
                                frequency: option.id
                                    ? option.id.endsWith('m')
                                        ? 'monthly'
                                        : 'weekly'
                                    : undefined,
                                dow: !option.id.endsWith('m') ? schedule.dow : undefined,
                                wom: option.id.endsWith('m') ? (schedule.wom ?? 1) : undefined,
                            })

                            if (option.id) updatedSchedule.setWeekDaysFromDate(schedule.date)

                            setSchedule(updatedSchedule)
                        }}
                    />
                </WorkflowPage.IconSection>

                {!!schedule.interval && (
                    <>
                        {schedule?.frequency === 'weekly' && (
                            <WorkflowPage.IconSection
                                title="Days of the week"
                                icon="calendar.large"
                            >
                                <div className="flex flex-wrap gap-2">
                                    {Schedule.dayStrings().map((option) => (
                                        <Chip
                                            size="small"
                                            key={option.id}
                                            title={option.label ?? ''}
                                            isSelected={isDayChecked(schedule.dow, option.id)}
                                            onClick={() => {
                                                if (!option.id) return
                                                const updatedSchedule = schedule.clone()
                                                updatedSchedule.weekDays = isDayChecked(
                                                    schedule.dow,
                                                    option.id
                                                )
                                                    ? updatedSchedule.weekDays.filter(
                                                          (s) => s !== option.id
                                                      )
                                                    : [...updatedSchedule.weekDays, option.id]
                                                setSchedule(updatedSchedule)
                                            }}
                                        />
                                    ))}
                                </div>
                            </WorkflowPage.IconSection>
                        )}

                        {schedule?.frequency === 'monthly' && (
                            <WorkflowPage.IconSection
                                title="Day of the month"
                                icon="calendar.large"
                            >
                                <div className="flex flex-wrap gap-2">
                                    {Schedule.weekMonthStrings(schedule.date).map((option) => (
                                        <Chip
                                            size="small"
                                            key={option.id}
                                            title={option.label ?? ''}
                                            isSelected={
                                                (schedule.wom?.toString() ?? '') === option.id
                                            }
                                            onClick={() => {
                                                if (!option.id) return
                                                setSchedule(
                                                    schedule.clone({
                                                        wom: parseInt(option.id),
                                                    })
                                                )
                                            }}
                                        />
                                    ))}
                                </div>
                            </WorkflowPage.IconSection>
                        )}

                        <WorkflowPage.IconSection title="Number of times" icon="hash.large">
                            <Select
                                size="medium"
                                align="end"
                                selectedOptionID={schedule.instances?.toString() ?? ''}
                                options={instancesOptions}
                                onSelectOption={(option) => {
                                    setSchedule(
                                        schedule.clone({
                                            instances: option.id === '' ? '' : parseInt(option.id),
                                        })
                                    )
                                }}
                            />
                        </WorkflowPage.IconSection>
                    </>
                )}
            </div>
        </Modal>
    )
}
